import React, { useState, useEffect } from 'react';
import { Plus, Building2, Users, Calendar, Globe, DollarSign, Ruler, MapPin, Edit2, Trash2, Leaf, Flame, Building, TreePine, Target, Layers, ShieldCheck, FlaskConical, AlertTriangle } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import api from '../../services/api';
import { VERTICAL_MODULES } from '../../config/navigation.config';
import { useAuthStore } from '../../store/authStore';

/** Safely pull a human-readable string out of any axios error shape. */
function extractErrorMessage(err: any, fallback: string): string {
  // Try the most specific field first, then broaden
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.message === 'string') return data.message;
  if (typeof err?.message === 'string') return err.message;
  return fallback;
}

// Icon map for vertical modules (Lucide components keyed by icon name)
const VERTICAL_ICONS: Record<string, React.ElementType> = {
  Layers,
  ShieldCheck,
  Leaf,
  Target,
  Flame,
  Building,
  TreePine,
};

interface Organization {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'trial';
  created_at: string;
  base_timezone?: string;
  base_currency?: string;
  measurement_system?: string;
  coordinate_system?: string;
  /** Module keys enabled for this organisation */
  enabled_modules?: string[];
  /** Number of approved (active) users in this organisation */
  approved_users_count?: number;
  /** settings JSONB — carries is_demo flag among other things */
  settings?: Record<string, any>;
}

interface OrganizationFormData {
  name: string;
  admin_email: string;
  admin_name: string;
  timezone: string;
  currency: string;
  measurement_system: string;
  coordinate_system: string;
  enabled_modules: string[];
  is_demo: boolean;
}

const EMPTY_FORM: OrganizationFormData = {
  name: '',
  admin_email: '',
  admin_name: '',
  timezone: 'UTC',
  currency: 'USD',
  measurement_system: 'metric',
  coordinate_system: 'WGS84',
  enabled_modules: ['mvp0_core', 'mvp0_enterprise'],
  is_demo: false,
};

const SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white';

export default function OrganizationProvisioningPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<OrganizationFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/organizations');
      setOrganizations(response.data.data);
      setError(null);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load organizations'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      admin_email: '',
      admin_name: '',
      timezone: org.base_timezone || 'UTC',
      currency: org.base_currency || 'USD',
      measurement_system: org.measurement_system || 'metric',
      coordinate_system: org.coordinate_system || 'WGS84',
      enabled_modules: org.enabled_modules?.length
        ? org.enabled_modules
        : ['mvp0_core', 'mvp0_enterprise'],
      is_demo: org.settings?.is_demo === true,
    });
    setShowCreateForm(true);
  };

  const handleCancelEdit = () => {
    setShowCreateForm(false);
    setEditingOrg(null);
    setFormData(EMPTY_FORM);
  };

  const handleDelete = async (orgId: string, orgName: string) => {
    if (!confirm(`Are you sure you want to delete "${orgName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/organizations/${orgId}`);
      setSuccess('Organization deleted successfully');
      await fetchOrganizations();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete organization'));
    }
  };

  const toggleModule = (moduleId: string) => {
    // Core-platform verticals are always enabled and cannot be toggled
    const vertical = VERTICAL_MODULES.find((v) => v.id === moduleId);
    if (!vertical || vertical.status === 'core') return;
    setFormData((prev) => ({
      ...prev,
      enabled_modules: prev.enabled_modules.includes(moduleId)
        ? prev.enabled_modules.filter((m) => m !== moduleId)
        : [...prev.enabled_modules, moduleId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { currentOrganizationId, setOrganizationModules } = useAuthStore.getState();
    try {
      if (editingOrg) {
        await api.put(`/organizations/${editingOrg.id}`, {
          name: formData.name,
          base_timezone: formData.timezone,
          base_currency: formData.currency,
          measurement_system: formData.measurement_system,
          coordinate_system: formData.coordinate_system,
          enabled_modules: formData.enabled_modules,
          is_demo: formData.is_demo,
        });
        // Keep the sidebar in sync: if we just edited the currently active org,
        // push the new module list into the auth store immediately.
        if (editingOrg.id === currentOrganizationId) {
          setOrganizationModules(formData.enabled_modules);
        }
        setSuccess('Organization updated successfully');
      } else {
        await api.post('/organizations', {
          name: formData.name,
          base_timezone: formData.timezone,
          base_currency: formData.currency,
          measurement_system: formData.measurement_system,
          coordinate_system: formData.coordinate_system,
          root_admin_email: formData.admin_email,
          is_demo: formData.is_demo,
        });
        setSuccess('Organization provisioned successfully');
      }
      setShowCreateForm(false);
      setEditingOrg(null);
      setFormData(EMPTY_FORM);
      await fetchOrganizations();
    } catch (err: any) {
      setError(
        extractErrorMessage(err, `Failed to ${editingOrg ? 'update' : 'provision'} organization`)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'trial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Organisation Provisioning
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Create and manage organisations. Select which industry verticals are enabled for each client.
          </p>
        </div>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          variant={showCreateForm ? 'outline' : 'primary'}
          className="flex items-center gap-2"
        >
          {showCreateForm ? 'Cancel' : <><Plus className="w-4 h-4" />Provision Organisation</>}
        </Button>
      </div>

      {/* Alerts */}
      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Create / Edit Form ──────────────────────────────────────────────── */}
      {showCreateForm && (
        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            {editingOrg ? 'Edit Organisation' : 'New Organisation'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Organisation & Admin Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Organisation Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Greenfield Estates"
                required
              />

              {!editingOrg && (
                <>
                  <Input
                    label="Admin Name"
                    value={formData.admin_name}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    placeholder="Jane Smith"
                    required
                  />
                  <Input
                    label="Admin Email"
                    type="email"
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    placeholder="admin@greenfield.com"
                    required
                  />
                </>
              )}

              {/* Localisation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className={SELECT_CLASS}
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="Europe/London">London</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Asia/Tokyo">Japan (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEST)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className={SELECT_CLASS}
                >
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — British Pound</option>
                  <option value="INR">INR — Indian Rupee</option>
                  <option value="JPY">JPY — Japanese Yen</option>
                  <option value="AUD">AUD — Australian Dollar</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Measurement System
                </label>
                <select
                  value={formData.measurement_system}
                  onChange={(e) =>
                    setFormData({ ...formData, measurement_system: e.target.value })
                  }
                  className={SELECT_CLASS}
                >
                  <option value="metric">Metric (metres, kg, hectares)</option>
                  <option value="imperial">Imperial (feet, lbs, acres)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Coordinate System
                </label>
                <select
                  value={formData.coordinate_system}
                  onChange={(e) =>
                    setFormData({ ...formData, coordinate_system: e.target.value })
                  }
                  className={SELECT_CLASS}
                >
                  <option value="WGS84">WGS84 (GPS Standard)</option>
                  <option value="UTM">UTM (Universal Transverse Mercator)</option>
                  <option value="EPSG:3857">Web Mercator</option>
                </select>
              </div>
            </div>

            {/* ── Industry Vertical Modules ───────────────────────────────── */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                Industry Verticals
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Select the verticals to enable for this organisation. Enabled verticals appear in
                the navigation sidebar for all users of this organisation.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {VERTICAL_MODULES.map((vertical) => {
                  const isCorePlatform = vertical.status === 'core';
                  const isEnabled = formData.enabled_modules.includes(vertical.id);
                  const IconComponent = VERTICAL_ICONS[vertical.icon];

                  return (
                    <button
                      key={vertical.id}
                      type="button"
                      onClick={() => toggleModule(vertical.id)}
                      disabled={isCorePlatform || vertical.status === 'coming_soon'}
                      className={[
                        'relative flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all',
                        isEnabled
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
                        isCorePlatform || vertical.status === 'coming_soon'
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:border-primary-400 cursor-pointer',
                      ].join(' ')}
                    >
                      {/* Icon */}
                      <div
                        className={[
                          'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
                          isEnabled
                            ? 'bg-primary-100 dark:bg-primary-800/40'
                            : 'bg-gray-100 dark:bg-gray-700',
                        ].join(' ')}
                      >
                        {IconComponent && (
                          <IconComponent
                            className={[
                              'w-5 h-5',
                              isEnabled
                                ? 'text-primary-600 dark:text-primary-400'
                                : 'text-gray-500 dark:text-gray-400',
                            ].join(' ')}
                          />
                        )}
                      </div>

                      {/* Label + description */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={[
                            'text-sm font-medium truncate',
                            isEnabled
                              ? 'text-primary-700 dark:text-primary-300'
                              : 'text-gray-900 dark:text-white',
                          ].join(' ')}
                        >
                          {vertical.label}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {vertical.description}
                        </p>
                      </div>

                      {/* Status pill */}
                      <span
                        className={[
                          'absolute top-2 right-2 px-1.5 py-0.5 text-xs rounded-full',
                          vertical.status === 'coming_soon'
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                            : isEnabled
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                        ].join(' ')}
                      >
                        {vertical.status === 'coming_soon'
                          ? 'Soon'
                          : isEnabled
                          ? 'On'
                          : 'Off'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Demo organisation toggle */}
            <div className={`pt-4 border-t ${formData.is_demo ? 'border-orange-300 dark:border-orange-700/50' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className={`rounded-lg p-3 ${formData.is_demo ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800/50' : 'bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700'}`}>
                <div className="flex items-start gap-3">
                  {/* Toggle */}
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      id="is_demo_toggle"
                      checked={formData.is_demo}
                      onChange={(e) => {
                        const next = e.target.checked;
                        const msg = next
                          ? 'Mark this organisation as a Demo environment?\n\nThis will make it available as a seed target in Demo Setup. Seed scripts can load large volumes of test data into it.\n\nOnly do this for dedicated demo orgs — never for production.'
                          : 'Remove the Demo flag from this organisation?\n\nIt will no longer appear in the Demo Setup seed dropdown.';
                        if (window.confirm(msg)) {
                          setFormData({ ...formData, is_demo: next });
                        }
                        // If user cancels, the checkbox reverts automatically because
                        // `checked` is controlled by formData.is_demo (unchanged).
                      }}
                    />
                    <div className={`w-10 h-6 rounded-full transition-colors ${formData.is_demo ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                    <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${formData.is_demo ? 'translate-x-4' : ''}`} />
                  </div>

                  {/* Label + description */}
                  <label htmlFor="is_demo_toggle" className="flex-1 cursor-pointer select-none">
                    <span className={`text-sm font-semibold flex items-center gap-1.5 ${formData.is_demo ? 'text-orange-700 dark:text-orange-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      <FlaskConical size={14} className={formData.is_demo ? 'text-orange-500' : 'text-gray-400'} />
                      Demo organisation
                      {formData.is_demo && (
                        <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-medium">
                          Enabled
                        </span>
                      )}
                    </span>
                    <p className={`text-xs mt-0.5 ${formData.is_demo ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {formData.is_demo
                        ? 'This organisation is marked as a demo environment. Seed scripts in Demo Setup can load test data into it.'
                        : 'Mark as demo to make this org available as a seed target in Demo Setup. Production orgs are never shown there.'}
                    </p>
                  </label>
                </div>

                {/* Warning banner — only shown when enabled */}
                {formData.is_demo && (
                  <div className="mt-2.5 flex items-start gap-2 rounded-md bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700/50 px-3 py-2">
                    <AlertTriangle size={13} className="text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-orange-700 dark:text-orange-300 leading-relaxed">
                      <strong>Demo environments only.</strong> Seed scripts will insert large volumes of synthetic test data into this organisation.
                      Never flag a production organisation as demo — doing so risks overwriting real customer data.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancelEdit}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? editingOrg
                    ? 'Updating...'
                    : 'Provisioning...'
                  : editingOrg
                  ? 'Update Organisation'
                  : 'Provision Organisation'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* ── Organisations List ──────────────────────────────────────────────── */}
      {!showCreateForm && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.map((org) => {
            const isDemo = org.settings?.is_demo === true;
            const divider = isDemo
              ? 'border-orange-200 dark:border-orange-800/40'
              : 'border-gray-200 dark:border-gray-700';
            return (
              <Card
                key={org.id}
                padding="none"
                className={isDemo ? 'border border-orange-300 dark:border-orange-700/50 overflow-hidden' : ''}
              >
                {/* Demo banner strip */}
                {isDemo && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 border-b border-orange-200 dark:border-orange-700/50">
                    <FlaskConical className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    <span className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide">
                      Demo Environment
                    </span>
                  </div>
                )}

                <div className={`p-6 space-y-4 ${isDemo ? 'bg-orange-50/40 dark:bg-orange-950/10' : ''}`}>

                  {/* Card header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isDemo ? 'bg-orange-100 dark:bg-orange-900/40' : 'bg-blue-100 dark:bg-blue-900'}`}>
                        {isDemo
                          ? <FlaskConical className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                          : <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {org.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          ID: {org.id.slice(0, 8)}…
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(org.status)}`}>
                      {org.status}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className={`grid grid-cols-2 gap-4 pt-4 border-t ${divider}`}>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Approved Users</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {org.approved_users_count ?? 0}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Created</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(org.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Localisation metadata */}
                  {(org.base_timezone || org.base_currency || org.measurement_system) && (
                    <div className={`pt-3 border-t ${divider} space-y-1.5`}>
                      {org.base_timezone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-400">{org.base_timezone}</span>
                        </div>
                      )}
                      {org.base_currency && (
                        <div className="flex items-center gap-2 text-sm">
                          <DollarSign className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-400">{org.base_currency}</span>
                        </div>
                      )}
                      {org.measurement_system && (
                        <div className="flex items-center gap-2 text-sm">
                          <Ruler className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-400 capitalize">
                            {org.measurement_system}
                          </span>
                        </div>
                      )}
                      {org.coordinate_system && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-400">
                            {org.coordinate_system}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Enabled verticals */}
                  <div className={`pt-3 border-t ${divider}`}>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
                      Enabled Verticals
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {VERTICAL_MODULES.filter((v) =>
                        (org.enabled_modules ?? []).includes(v.id)
                      ).map((v) => {
                        const IconComponent = VERTICAL_ICONS[v.icon];
                        return (
                          <span
                            key={v.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                            title={v.description}
                          >
                            {IconComponent && <IconComponent className="w-3 h-3" />}
                            {v.label}
                          </span>
                        );
                      })}
                      {!(org.enabled_modules ?? []).some((m) =>
                        VERTICAL_MODULES.map((v) => v.id).includes(m)
                      ) && (
                        <span className="text-xs text-gray-400 italic">No verticals enabled</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={`pt-3 border-t ${divider} flex gap-2`}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(org)}
                      className="flex-1"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDelete(org.id, org.name)}
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>

                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {organizations.length === 0 && !showCreateForm && (
        <Card>
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Organisations Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Get started by provisioning your first organisation
            </p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Provision Organisation
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
